export type ApiError = {
  error: {
    code: string;
    message: string;
  };
};

export const accessTokenStorageKey = "our-money-access-token";
export const tokenRefreshedEvent = "our-money:token-refreshed";

export class ApiRequestError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

export async function api<T>(
  path: string,
  options: Omit<RequestInit, "body"> & {
    token?: string | null;
    body?: BodyInit | Record<string, unknown> | null;
  } = {}
): Promise<T> {
  const response = await request<T>(path, options);
  if (response.ok) return response.data as T;

  if (response.status === 401 && options.token && !isAuthPath(path)) {
    const refreshedToken = await refreshTokenForRetry();
    const retried = await request<T>(path, { ...options, token: refreshedToken });
    if (retried.ok) return retried.data as T;
    throw new ApiRequestError(retried.message, retried.status, retried.code);
  }

  throw new ApiRequestError(response.message, response.status, response.code);
}

async function request<T>(
  path: string,
  options: Omit<RequestInit, "body"> & {
    token?: string | null;
    body?: BodyInit | Record<string, unknown> | null;
  }
) {
  const headers = new Headers(options.headers);
  if (options.token) headers.set("Authorization", `Bearer ${options.token}`);
  let body = options.body as BodyInit | null | undefined;
  if (body && typeof body === "object" && !(body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(body);
  }

  const response = await fetch(path, {
    ...options,
    headers,
    body,
    credentials: "include"
  });
  const payload = (await response.json().catch(() => ({}))) as { data?: T } & ApiError;
  return {
    ok: response.ok,
    status: response.status,
    code: payload.error?.code ?? "ERROR",
    message: payload.error?.message ?? "请求失败",
    data: payload.data
  };
}

async function refreshTokenForRetry() {
  const response = await request<{ access_token: string }>("/api/auth/refresh", { method: "POST" });
  if (!response.ok || !response.data?.access_token) {
    localStorage.removeItem(accessTokenStorageKey);
    throw new ApiRequestError(response.message, response.status, response.code);
  }
  localStorage.setItem(accessTokenStorageKey, response.data.access_token);
  window.dispatchEvent(new CustomEvent(tokenRefreshedEvent, { detail: response.data.access_token }));
  return response.data.access_token;
}

function isAuthPath(path: string) {
  return path.startsWith("/api/auth/") || path.startsWith("/api/setup");
}
